import { Power2 } from 'gsap';
import * as PIXI from 'pixi.js';

import DraggablePreset from './DraggablePreset';
import Rectangle from '../../graphics/Rectangle';
import TextBlock from '../../graphics/TextBlock';
import {
  getCenterX,
  getCenterY,
  getFixBottomY,
  getFixTopY,
  getXAccountingForAnchor,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 3;
const ANIMATION_TIMING_RATIOS = {
  words: 3,
  lines: 2,
  zoom: 1,
};

export default class WordBlock extends DraggablePreset {
  constructor(app) {
    super(app);

    this.topLine = new Rectangle();
    this.bottomLine = new Rectangle();

    this.textBlock = new TextBlock(app);

    this.zoomableContainer = new PIXI.Container();
    this.zoomedGraphics = new PIXI.Graphics();

    this.zoomableContainer.addChild(this.zoomedGraphics, this.textBlock);
    this.container.addChild(this.zoomableContainer);
  }

  calculateAnimationTimings() {
    const { words, lines, zoom } = ANIMATION_TIMING_RATIOS;
    const ratioDenominator = words + lines + zoom;

    const halfDuration = Math.min(this.duration, MAX_DURATION) / 2;

    return {
      words: (halfDuration * words) / ratioDenominator,
      lines: (halfDuration * lines) / ratioDenominator,
      zoom: (halfDuration * zoom) / ratioDenominator,
    };
  }

  update(model) {
    model = {
      ...model,
      anchor: { x: 0.5, y: 0.5 }, // center anchor because it should grow outwardly. NOTE: this means scaling happens from center.
    };
    super.update(model);

    const animationTimings = this.calculateAnimationTimings();

    this.textBlock.updateTextAndStyle(model.text, model);
    const lineThickness = this.textBlock.lineHeight / 5;
    this.setDraggableRoot(
      this.textBlock.blockHeight + lineThickness * 4,
      this.textBlock.blockWidth
    );
    this.textBlock.x = getCenterX(
      { width: this.textBlock.blockWidth },
      this.root
    );
    this.textBlock.y = getCenterY(
      { height: this.textBlock.blockHeight },
      this.root
    );

    this.textBlock.forEachWord((text, { index }) => {
      text.alpha = 0;
      text.lineControls.y += text.fontSize;
      this.animate(text.lineControls)
        .to(
          {
            alpha: 1,
            y: `-=${text.fontSize}`,
            ease: Power2.easeInOut,
            duration: (animationTimings.words * 1.5) / this.textBlock.numWords,
          },
          (animationTimings.words * index) / this.textBlock.numWords
        )
        .reverseOut();
    });

    [this.topLine, this.bottomLine].forEach((emphasisLine) => {
      emphasisLine.color = this.secondaryColor;
      emphasisLine.height = lineThickness;
      emphasisLine.width = 0;
      emphasisLine.x = getCenterX(emphasisLine, this.root);
      this.animate(emphasisLine)
        .to(
          {
            width: this.root.width,
            x: getXAccountingForAnchor(this.root),
            duration: animationTimings.lines,
            ease: Power2.easeInOut,
          },
          animationTimings.words
        )
        .reverseOut();
    });
    this.topLine.y = getFixTopY(this.topLine, this.root);
    this.bottomLine.y = getFixBottomY(this.bottomLine, this.root);

    // add zoom effect
    this.zoomableContainer.scale.x = 0.8;
    this.zoomableContainer.scale.y = 0.8;
    this.animate(this.zoomableContainer.scale)
      .to(
        {
          x: 1,
          y: 1,
          duration: animationTimings.zoom,
          ease: Power2.easeInOut,
        },
        animationTimings.words + animationTimings.lines
      )
      .reverseOut();
  }

  render() {
    super.render();
    this.zoomedGraphics.clear();
    this.topLine.draw(this.zoomedGraphics);
    this.bottomLine.draw(this.zoomedGraphics);
  }
}
